import { useTranslation } from "next-i18next";
import Head from "next/head";
import { useRouter } from "next/router";

import { Box } from "@filadd/phi-plus-styled-system/jsx";

import { useCourse } from "hooks/courses-new/course";
import { useLayoutFeatures } from "hooks/layout";
import { getThemeFont } from "themes/fonts";

import MainLayout from "./MainLayout";

const font = getThemeFont();

interface LayoutProps {
  courseSlug: string;
  children: JSX.Element;
  ssrCookies: Record<string, string>;
}

const getLayoutName = (pathname: string) => {
  switch (pathname) {
    case "/courses/[courseSlug]/units/[programUnitPivotShortSlug]/blocks/[blocksPivotShortSlug]": {
      return "unit_content_view_layout";
    }
    default: {
      return "main_layout";
    }
  }
};

export const Layout = ({ courseSlug, children, ssrCookies }: LayoutProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const layoutName = getLayoutName(router.pathname);

  const { data: courseData } = useCourse({
    params: { field: "slug", value: courseSlug },
  });

  const layoutFeaturesResult = useLayoutFeatures(
    courseSlug,
    Boolean(courseData),
  );

  if (!courseData) {
    return <div>Layout error: Failed to fetch course data</div>;
  }

  if (!layoutFeaturesResult || !layoutFeaturesResult.data) {
    return <div>Layout error: Failed to fetch layout features</div>;
  }

  const layoutFeatures = layoutFeaturesResult.data;

  return (
    <Box className={font.className}>
      <Head>
        <title>{courseData.short_title}</title>
        <meta
          property="og:title"
          content={t<string>("common:pwa.appName")}
          key="title"
        />
      </Head>

      <MainLayout
        layoutName={layoutName}
        layoutFeatures={layoutFeatures}
        ssrCookies={ssrCookies}
      >
        {children}
      </MainLayout>
    </Box>
  );
};
